/**
 * ── COLOURS THAT ARE VERA SPEAKING, NEVER THE TENANT'S ───────────────────────────
 *
 * Fixed hexes, and the fixedness is the point rather than an oversight.
 *
 * ⚠️ DO NOT ROUTE THESE THROUGH `theme.palette.*`, INCLUDING `common.white` AND
 * `text.primary`. Every colour hook this app exposes is, directly or by derivation,
 * the TENANT'S: `text.primary` IS their Studio `text_color`, `background.default`
 * their `bg_color`, `background.paper` is derived from it, `primary.main` their
 * `primary_color`, and `brand[*]` is derived from `primary_color` by
 * `settings/mui/brandRamp.ts`. See `settings/tokens.ts`, which documents that routing
 * as the white-label promise.
 *
 * A handful of elements are Vera attesting to something rather than the brand
 * decorating its own page — the authenticity chip, the demo disclosure bar. Those must
 * not look brand-authored, and their legibility must not be a function of a colour a
 * tenant picked.
 *
 * ── The failure this module exists to prevent, measured ──────────────────────────
 * `AuthenticChip` took a fixed white fill (correct — see its `onImage` docblock) and a
 * `color: 'text.primary'` label (not correct). On a Studio theme with
 * `text_color: '#FFFFFF'` the label therefore rendered WHITE ON WHITE: measured in the
 * browser at a contrast ratio of exactly **1.000**, with the NFC glyph — then
 * `text.secondary`, derived from the same `text_color` — at #D6CFCF, about 1.5:1.
 * The word was painted and invisible, so the chip read as an empty capsule.
 *
 * Half-pinned is the worst of the two options: a fully inherited chip would at least
 * have been legible. Pin the whole mark or none of it.
 *
 * `DemoBanner` reached these same three values independently and documented the same
 * reasoning; they are shared here rather than declared twice (working-conventions §36,
 * and §44's single-definition argument one layer down).
 */

/** The mark's own surface. White, and never `common.white`. */
export const VERA_SURFACE = '#FFFFFF'

/**
 * The mark's ink. Pure black, not a near-black.
 *
 * Load-bearing for `AuthenticChip`'s foil sheen, whose peak alpha is a contrast
 * budget computed against BLACK text on white: `mix-blend-mode: screen` at 0.40 white
 * takes black to #737373 (4.74:1, over the AA floor) for the instant the band crosses
 * a letter. A lighter ink starts that arithmetic above the floor rather than below it.
 */
export const VERA_INK = '#000000'

/**
 * Secondary ink — a glyph or a sub-label on `VERA_SURFACE`.
 *
 * 7.6:1 on white, so it clears the 4.5:1 text floor as well as the 3:1 non-text bar a
 * glyph is actually held to. Chosen over the #707070 the themed `text.secondary`
 * happened to resolve to on the default theme, which measured 4.6:1 and dropped to
 * about 2.3:1 under the sheen's peak; this one holds ~2.9:1 there.
 */
export const VERA_INK_MUTED = '#52525B'
